import { useState, useCallback, useEffect, useMemo } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import { Button, Input, Modal, Select, Table, Alert, Text, Toggle, Badge, Checkbox, ConfirmDialog, DatePicker, type TableColumn } from '@/ui'
import { DEFAULT_PRODUCT_CATALOG, CreditPromotionListSchema, type CreditPromotion } from '@fiket/shared/schemas'
import { apiFetch } from '@/lib/api'
import styles from './Settings.module.css'

interface AppSetting {
  key: string
  value: unknown
  category: string
  label: string
  description: string | null
  createdAt: string
  updatedAt: string
}

type SettingsResponse = AppSetting[]

// ── 가입 설정 섹션 ──────────────────────────────────────────
function RegistrationSettings() {
  const { addToast } = useToast()
  const { data, isLoading } = useApiFetch<SettingsResponse>('/api/admin/settings?category=REGISTRATION')
  const { trigger, isLoading: saving } = useApiMutation('/api/admin/settings', 'PATCH')
  const [freeCredits, setFreeCredits] = useState('')

  useEffect(() => {
    if (!data) return
    const setting = data.find((s) => s.key === 'signup_free_credits')
    setFreeCredits(setting ? String(setting.value) : '0')
  }, [data])

  const handleSave = useCallback(async () => {
    const num = parseInt(freeCredits, 10)
    if (isNaN(num) || num < 0) {
      addToast({ type: 'error', message: '0 이상의 정수를 입력하세요.' })
      return
    }
    const result = await trigger({
      key: 'signup_free_credits',
      value: num,
      category: 'REGISTRATION',
      label: '신규 가입 무료 크레딧',
    })
    if (result) {
      addToast({ type: 'success', message: '가입 크레딧 설정이 저장되었습니다.' })
    } else {
      addToast({ type: 'error', message: '저장에 실패했습니다.' })
    }
  }, [freeCredits, trigger, addToast])

  if (isLoading) return null

  return (
    <div className={styles.section}>
      <Text variant="heading-sm">가입 설정</Text>
      <div className={styles.sectionRow}>
        <Input
          label="신규 가입 무료 크레딧"
          type="number"
          min={0}
          value={freeCredits}
          onChange={(e) => setFreeCredits(e.target.value)}
          style={{ maxWidth: 160 }}
        />
        <Button size="sm" loading={saving} onClick={handleSave}>저장</Button>
      </div>
    </div>
  )
}

// ── 화환 주문 URL 섹션 ──────────────────────────────────────
// 청첩장 화환 섹션의 전역 기본 주문 URL. 사용자가 화환 URL을 비웠을 때 fallback으로 쓰인다.
// key: wreath_order_url, category: SYSTEM. /letter 렌더 경로는 5분 TTL 캐시(FIKET-169) —
// PATCH /api/admin/settings 저장 시 서버가 invalidateWreathOrderUrlCache 로 즉시 반영한다.
function WreathSettings() {
  const { addToast } = useToast()
  const { data, isLoading } = useApiFetch<SettingsResponse>('/api/admin/settings?category=SYSTEM')
  const { trigger, isLoading: saving } = useApiMutation('/api/admin/settings', 'PATCH')
  const [url, setUrl] = useState('')

  useEffect(() => {
    if (!data) return
    const setting = data.find((s) => s.key === 'wreath_order_url')
    setUrl(setting ? String(setting.value) : '')
  }, [data])

  const handleSave = useCallback(async () => {
    const trimmed = url.trim()
    // 빈 값은 허용(기능 비활성). 값이 있으면 http/https만 허용.
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      addToast({ type: 'error', message: 'http 또는 https로 시작하는 URL을 입력하세요.' })
      return
    }
    const result = await trigger({
      key: 'wreath_order_url',
      value: trimmed,
      category: 'SYSTEM',
      label: '화환 주문 기본 URL',
    })
    if (result) {
      addToast({ type: 'success', message: '화환 주문 URL이 저장되었습니다.' })
    } else {
      addToast({ type: 'error', message: '저장에 실패했습니다.' })
    }
  }, [url, trigger, addToast])

  if (isLoading) return null

  return (
    <div className={styles.section}>
      <Text variant="heading-sm">화환 주문 URL</Text>
      <Text variant="body-sm" color="secondary">
        청첩장 화환 섹션의 전역 기본 주문 링크입니다. 사용자가 직접 URL을 입력하지 않으면 이 값이 사용됩니다.
      </Text>
      <div className={styles.sectionRow}>
        <Input
          label="화환 주문 기본 URL"
          type="url"
          value={url}
          placeholder="https://..."
          onChange={(e) => setUrl(e.target.value)}
          style={{ maxWidth: 480, flex: 1 }}
        />
        <Button size="sm" loading={saving} onClick={handleSave}>저장</Button>
      </div>
    </div>
  )
}

// ── 워터마크 문구·링크 섹션 ──────────────────────────────────
// 미활성(시안 미리보기) 청첩장/감사장 뷰어의 "워터마크 제거" 버튼 문구·링크를 각각 편집.
function WatermarkSettings() {
  const { addToast } = useToast()
  const { data, isLoading } = useApiFetch<SettingsResponse>('/api/admin/settings?category=watermark')
  const { trigger, isLoading: saving } = useApiMutation('/api/admin/settings', 'PATCH')

  const [invButton, setInvButton] = useState('')
  const [invLink, setInvLink] = useState('')
  const [thxButton, setThxButton] = useState('')
  const [thxLink, setThxLink] = useState('')

  useEffect(() => {
    if (!data) return
    const cfg = data.find((s) => s.key === 'watermark_config')?.value as
      | { invitation?: { buttonText?: string; linkUrl?: string }; thankyou?: { buttonText?: string; linkUrl?: string } }
      | undefined
    const DEFAULT_BTN = '워터마크 제거하러가기'
    const DEFAULT_URL = 'https://smartstore.naver.com/fiket/products/13257373714'
    setInvButton(cfg?.invitation?.buttonText ?? DEFAULT_BTN)
    setInvLink(cfg?.invitation?.linkUrl ?? DEFAULT_URL)
    setThxButton(cfg?.thankyou?.buttonText ?? DEFAULT_BTN)
    setThxLink(cfg?.thankyou?.linkUrl ?? DEFAULT_URL)
  }, [data])

  const handleSave = useCallback(async () => {
    const isHttpUrl = (u: string) => /^https?:\/\//i.test(u.trim())
    if (!invButton.trim() || !thxButton.trim()) {
      addToast({ type: 'error', message: '버튼 문구를 입력하세요.' })
      return
    }
    if (!isHttpUrl(invLink) || !isHttpUrl(thxLink)) {
      addToast({ type: 'error', message: 'http/https 링크를 입력하세요.' })
      return
    }
    const result = await trigger({
      key: 'watermark_config',
      value: {
        invitation: { buttonText: invButton.trim(), linkUrl: invLink.trim() },
        thankyou: { buttonText: thxButton.trim(), linkUrl: thxLink.trim() },
      },
      category: 'watermark',
      label: '워터마크 문구·링크',
      description: '미활성 청첩장/감사장 뷰어의 워터마크 제거 버튼 문구·링크',
    })
    if (result) {
      addToast({ type: 'success', message: '워터마크 설정이 저장되었습니다.' })
    } else {
      addToast({ type: 'error', message: '저장에 실패했습니다.' })
    }
  }, [invButton, invLink, thxButton, thxLink, trigger, addToast])

  if (isLoading) return null

  return (
    <div className={styles.section}>
      <Text variant="heading-sm">워터마크 (시안 미리보기)</Text>
      <Text variant="body-sm">미활성 청첩장/감사장 뷰어에 뜨는 "워터마크 제거" 버튼 문구·링크를 각각 설정합니다.</Text>
      <div className={styles.sectionRow}>
        <Input
          label="청첩장 버튼 문구"
          value={invButton}
          onChange={(e) => setInvButton(e.target.value)}
          maxLength={40}
          style={{ maxWidth: 240 }}
        />
        <Input
          label="청첩장 링크"
          value={invLink}
          onChange={(e) => setInvLink(e.target.value)}
          placeholder="https://..."
          style={{ flex: 1, minWidth: 480, width: '100%' }}
        />
      </div>
      <div className={styles.sectionRow}>
        <Input
          label="감사장 버튼 문구"
          value={thxButton}
          onChange={(e) => setThxButton(e.target.value)}
          maxLength={40}
          style={{ maxWidth: 240 }}
        />
        <Input
          label="감사장 링크"
          value={thxLink}
          onChange={(e) => setThxLink(e.target.value)}
          placeholder="https://..."
          style={{ flex: 1, minWidth: 480, width: '100%' }}
        />
      </div>
      <div className={styles.sectionRow}>
        <Button size="sm" loading={saving} onClick={handleSave}>저장</Button>
      </div>
    </div>
  )
}

// ── 점검 모드 섹션 ──────────────────────────────────────────
function MaintenanceSettings() {
  const { addToast } = useToast()
  const { data, isLoading, refetch } = useApiFetch<SettingsResponse>('/api/admin/settings?category=SYSTEM')
  const { trigger, isLoading: saving } = useApiMutation('/api/admin/settings', 'PATCH')

  const enabled = data?.find((s) => s.key === 'maintenance_enabled')?.value === true
  // 키가 없으면 기본 노출(true) — 이미 출시된 기능
  const thankyouMenuEnabled = data?.find((s) => s.key === 'thankyou_menu_enabled')?.value !== false
  const purchaseMenuEnabled = data?.find((s) => s.key === 'purchase_menu_enabled')?.value !== false

  const handleToggle = useCallback(async (checked: boolean) => {
    const result = await trigger({
      key: 'maintenance_enabled',
      value: checked,
      category: 'SYSTEM',
      label: '서비스 점검 모드',
    })
    if (result) {
      addToast({ type: 'success', message: checked ? '점검 모드가 활성화되었습니다.' : '점검 모드가 해제되었습니다.' })
      refetch()
    } else {
      addToast({ type: 'error', message: '설정 변경에 실패했습니다.' })
    }
  }, [trigger, addToast, refetch])

  const handleThankyouMenuToggle = useCallback(async (checked: boolean) => {
    const result = await trigger({
      key: 'thankyou_menu_enabled',
      value: checked,
      category: 'SYSTEM',
      label: '감사장 메뉴 노출',
    })
    if (result) {
      addToast({ type: 'success', message: checked ? '감사장 메뉴가 노출됩니다.' : '감사장 메뉴가 숨겨집니다.' })
      refetch()
    } else {
      addToast({ type: 'error', message: '설정 변경에 실패했습니다.' })
    }
  }, [trigger, addToast, refetch])

  const handlePurchaseMenuToggle = useCallback(async (checked: boolean) => {
    const result = await trigger({
      key: 'purchase_menu_enabled',
      value: checked,
      category: 'SYSTEM',
      label: '구매하기 메뉴 노출',
    })
    if (result) {
      addToast({ type: 'success', message: checked ? '구매하기 메뉴가 노출됩니다.' : '구매하기 메뉴가 숨겨집니다.' })
      refetch()
    } else {
      addToast({ type: 'error', message: '설정 변경에 실패했습니다.' })
    }
  }, [trigger, addToast, refetch])

  if (isLoading) return null

  return (
    <div className={styles.section}>
      <Text variant="heading-sm">점검 모드</Text>
      <div className={styles.sectionRow}>
        <Toggle
          checked={enabled}
          onChange={handleToggle}
          disabled={saving}
          label="서비스 점검 모드 활성화"
        />
      </div>
      {enabled && (
        <Alert variant="warning">
          점검 모드가 활성화되어 있습니다. 관리자를 제외한 모든 사용자에게 점검 페이지가 표시됩니다.
        </Alert>
      )}

      <Text variant="heading-sm">감사장 메뉴</Text>
      <div className={styles.sectionRow}>
        <Toggle
          checked={thankyouMenuEnabled}
          onChange={handleThankyouMenuToggle}
          disabled={saving}
          label="감사장 메뉴 노출"
        />
      </div>
      {!thankyouMenuEnabled && (
        <Alert variant="info">
          감사장 메뉴가 숨겨져 있습니다. 헤더·하단 네비게이션에서 노출되지 않습니다. (페이지 직접 접근은 가능)
        </Alert>
      )}

      <Text variant="heading-sm">구매하기 메뉴</Text>
      <div className={styles.sectionRow}>
        <Toggle
          checked={purchaseMenuEnabled}
          onChange={handlePurchaseMenuToggle}
          disabled={saving}
          label="구매하기 메뉴 노출"
        />
      </div>
      {!purchaseMenuEnabled && (
        <Alert variant="info">
          구매하기 메뉴가 숨겨져 있습니다. 헤더·하단 네비게이션에서 노출되지 않습니다. (페이지 직접 접근은 가능)
        </Alert>
      )}
    </div>
  )
}

// ── 구매 보너스 프로모션 섹션 ───────────────────────────────────
const EMPTY_PROMOTION: CreditPromotion = {
  id: '',
  name: '',
  paymentTypes: [],
  bonusCredits: 1,
  startsAt: '',
  endsAt: '',
  enabled: true,
}

// 소급 지급 관련 타입
interface BackfillWarning {
  promotionId: string
  name: string
  totalUsers: number
  totalCredits: number
}

interface BackfillPreviewUser {
  userId: string
  name: string | null
  email: string
  missedPayments: number
  creditsToGrant: number
}

interface BackfillPreview {
  users: BackfillPreviewUser[]
  totalUsers: number
  totalCredits: number
}

// ── 매출 리포트 수신자 섹션 ──────────────────────────────────
// 주간(월 09:00) 자동 리포트 + 유입경로 분석 페이지 '이메일 발송'의 수신 목록.
// app_settings.revenue_report_emails (string[]) 를 통째로 PATCH 한다.
function RevenueReportRecipients() {
  const { addToast } = useToast()
  const { data, isLoading, refetch } = useApiFetch<SettingsResponse>('/api/admin/settings?category=REPORT')
  const { trigger, isLoading: saving } = useApiMutation('/api/admin/settings', 'PATCH')
  const [emails, setEmails] = useState<string[]>([])
  const [newEmail, setNewEmail] = useState('')

  useEffect(() => {
    if (!data) return
    const setting = data.find((s) => s.key === 'revenue_report_emails')
    setEmails(Array.isArray(setting?.value) ? (setting.value as string[]) : [])
  }, [data])

  const save = useCallback(async (next: string[]) => {
    const result = await trigger({
      key: 'revenue_report_emails',
      value: next,
      category: 'REPORT',
      label: '매출 리포트 수신 이메일',
      description: '주간 매출 리포트(월 09:00)와 유입경로 분석 이메일 발송의 수신자 목록',
    })
    if (result) {
      setEmails(next)
      addToast({ type: 'success', message: '수신자 목록이 저장되었습니다.' })
      refetch()
    } else {
      addToast({ type: 'error', message: '저장에 실패했습니다.' })
    }
  }, [trigger, addToast, refetch])

  const addEmail = useCallback(() => {
    const email = newEmail.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      addToast({ type: 'error', message: '올바른 이메일 형식이 아닙니다.' })
      return
    }
    if (emails.includes(email)) {
      addToast({ type: 'error', message: '이미 등록된 이메일입니다.' })
      return
    }
    setNewEmail('')
    void save([...emails, email])
  }, [newEmail, emails, save, addToast])

  const removeEmail = useCallback((email: string) => {
    void save(emails.filter((e) => e !== email))
  }, [emails, save])

  if (isLoading) return null

  return (
    <div className={styles.section}>
      <Text variant="heading-sm">매출 리포트 수신자</Text>
      <Text variant="body-sm" color="tertiary">
        매주 월요일 09:00 주간 리포트와 유입경로 분석 페이지의 &apos;이메일 발송&apos;이 이 목록으로 발송됩니다.
        비어 있으면 자동 발송이 건너뛰어집니다.
      </Text>
      {emails.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-2)' }}>
          {emails.map((email) => (
            <li key={email} style={{ display: 'flex', alignItems: 'center', gap: 'var(--ds-space-3)' }}>
              <span style={{ fontSize: 'var(--ds-font-size-sm)' }}>{email}</span>
              <Button size="sm" variant="ghost" disabled={saving} onClick={() => removeEmail(email)}>
                삭제
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className={styles.sectionRow}>
        <Input
          placeholder="email@example.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addEmail() }}
          style={{ maxWidth: 280 }}
        />
        <Button size="sm" loading={saving} onClick={addEmail}>추가</Button>
      </div>
    </div>
  )
}

function PromotionSettings() {
  const { addToast } = useToast()
  const { data, isLoading, refetch } = useApiFetch<SettingsResponse>('/api/admin/settings?category=PROMOTION')
  const { trigger, isLoading: saving } = useApiMutation('/api/admin/settings', 'PATCH')

  const promotions = useMemo<CreditPromotion[]>(() => {
    const raw = data?.find((s) => s.key === 'credit_promotions')?.value
    const parsed = CreditPromotionListSchema.safeParse(raw)
    return parsed.success ? parsed.data : []
  }, [data])

  const [modalOpen, setModalOpen] = useState(false)
  const [draft, setDraft] = useState<CreditPromotion>(EMPTY_PROMOTION)
  const [isNew, setIsNew] = useState(true)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  // 소급 지급 상태
  const [warnings, setWarnings] = useState<BackfillWarning[]>([])
  const [backfillTarget, setBackfillTarget] = useState<{ promotionId: string; name: string } | null>(null)
  const [preview, setPreview] = useState<BackfillPreview | null>(null)
  const [applying, setApplying] = useState(false)

  // 시작/종료일을 한국 로컬 날짜로 표시 (저장값은 UTC ISO — 시작 00:00:00 / 종료 23:59:59)
  const formatLocalDate = (iso: string) =>
    new Date(iso).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  // 선택한 날짜를 시작(자정)/종료(하루 끝) 시각의 ISO 문자열로 변환
  const toStartIso = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString()
  const toEndIso = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString()

  const openNew = useCallback(() => {
    setDraft({ ...EMPTY_PROMOTION, id: crypto.randomUUID() })
    setIsNew(true)
    setModalOpen(true)
  }, [])

  const openEdit = useCallback((promo: CreditPromotion) => {
    setDraft(promo)
    setIsNew(false)
    setModalOpen(true)
  }, [])

  // 목록 전체를 저장 (credit_promotions 키 통째 덮어쓰기)
  const persist = useCallback(async (next: CreditPromotion[]) => {
    const result = await trigger({
      key: 'credit_promotions',
      value: next,
      category: 'PROMOTION',
      label: '구매 보너스 크레딧 프로모션',
    })
    if (result) {
      addToast({ type: 'success', message: '프로모션이 저장되었습니다.' })
      refetch()
      // 서버가 반환하는 warnings (과거 미지급 감지) 반영
      const w = (result as { warnings?: BackfillWarning[] }).warnings ?? []
      setWarnings(w)
      return true
    }
    addToast({ type: 'error', message: '저장에 실패했습니다.' })
    return false
  }, [trigger, addToast, refetch])

  const handleSaveDraft = useCallback(async () => {
    if (!draft.name.trim()) {
      addToast({ type: 'error', message: '프로모션 이름을 입력하세요.' })
      return
    }
    if (draft.paymentTypes.length === 0) {
      addToast({ type: 'error', message: '대상 상품을 1개 이상 선택하세요.' })
      return
    }
    if (!draft.startsAt || !draft.endsAt || draft.startsAt >= draft.endsAt) {
      addToast({ type: 'error', message: '시작일과 종료일을 올바르게 설정하세요.' })
      return
    }
    if (!Number.isInteger(draft.bonusCredits) || draft.bonusCredits < 1) {
      addToast({ type: 'error', message: '보너스 크레딧은 1 이상의 정수여야 합니다.' })
      return
    }
    const next = isNew
      ? [...promotions, draft]
      : promotions.map((p) => (p.id === draft.id ? draft : p))
    const ok = await persist(next)
    if (ok) setModalOpen(false)
  }, [draft, isNew, promotions, persist, addToast])

  const handleDelete = useCallback(async (id: string) => {
    await persist(promotions.filter((p) => p.id !== id))
  }, [promotions, persist])

  const toggleProductType = useCallback((type: string) => {
    setDraft((d) => ({
      ...d,
      paymentTypes: d.paymentTypes.includes(type as CreditPromotion['paymentTypes'][number])
        ? d.paymentTypes.filter((t) => t !== type)
        : [...d.paymentTypes, type as CreditPromotion['paymentTypes'][number]],
    }))
  }, [])

  // 소급 지급 미리보기 모달 열기
  const openBackfill = useCallback(async (promotionId: string, name: string) => {
    setBackfillTarget({ promotionId, name })
    setPreview(null)
    try {
      const res = await apiFetch<BackfillPreview>(
        '/api/admin/promotions/backfill/preview',
        { method: 'POST', body: JSON.stringify({ promotionId }) },
      )
      setPreview(res)
    } catch {
      addToast({ type: 'error', message: '미리보기를 불러오지 못했습니다.' })
      setBackfillTarget(null)
    }
  }, [addToast])

  // 소급 지급 실행
  const confirmBackfill = useCallback(async () => {
    if (!backfillTarget) return
    setApplying(true)
    try {
      const res = await apiFetch<{ granted: number; skipped: number; totalCredits: number }>(
        '/api/admin/promotions/backfill/apply',
        { method: 'POST', body: JSON.stringify({ promotionId: backfillTarget.promotionId }) },
      )
      addToast({ type: 'success', message: `소급 지급 완료: ${res.granted}건 / ${res.totalCredits}크레딧` })
      setWarnings((ws) => ws.filter((w) => w.promotionId !== backfillTarget.promotionId))
      setBackfillTarget(null)
    } catch {
      addToast({ type: 'error', message: '소급 지급에 실패했습니다.' })
    } finally {
      setApplying(false)
    }
  }, [backfillTarget, addToast])

  const columns: TableColumn<CreditPromotion>[] = [
    { key: 'name', label: '이름' },
    {
      key: 'paymentTypes',
      label: '대상 상품',
      render: (row) => row.paymentTypes
        .map((t) => DEFAULT_PRODUCT_CATALOG.find((p) => p.type === t)?.nameKo ?? t)
        .join(', '),
    },
    { key: 'bonusCredits', label: '보너스', width: '80px', render: (row) => `+${row.bonusCredits}` },
    {
      key: 'period',
      label: '기간',
      render: (row) => `${formatLocalDate(row.startsAt)} ~ ${formatLocalDate(row.endsAt)}`,
    },
    {
      key: 'enabled',
      label: '상태',
      width: '70px',
      render: (row) => <Badge color={row.enabled ? 'green' : 'gray'}>{row.enabled ? 'ON' : 'OFF'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      width: '110px',
      render: (row) => (
        <span className={styles.rowActions}>
          <button className={styles.editLink} onClick={() => openEdit(row)}>편집</button>
          <button className={styles.deleteLink} onClick={() => setDeleteTarget(row.id)}>삭제</button>
        </span>
      ),
    },
  ]

  if (isLoading) return null

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Text variant="heading-sm">구매 보너스 프로모션</Text>
        <Button size="sm" onClick={openNew}>프로모션 추가</Button>
      </div>
      {warnings.length > 0 && (
        <Alert variant="warning" title="과거 결제 미지급 감지">
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            {warnings.map((w) => (
              <li key={w.promotionId} style={{ marginBottom: '4px' }}>
                {w.name}: {w.totalUsers}명 / {w.totalCredits}크레딧 미지급{' '}
                <Button size="sm" variant="outline" onClick={() => openBackfill(w.promotionId, w.name)}>소급 지급</Button>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Table<CreditPromotion>
        columns={columns}
        data={promotions}
        rowKey={(row) => row.id}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={isNew ? '프로모션 추가' : '프로모션 편집'} size="sm">
        <div className={styles.modalContent}>
          <Input
            label="이름"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
          <div>
            <Text variant="body-sm">대상 상품</Text>
            <div className={styles.productList}>
              {DEFAULT_PRODUCT_CATALOG.map((p) => (
                <Checkbox
                  key={p.type}
                  label={p.nameKo}
                  checked={draft.paymentTypes.includes(p.type as CreditPromotion['paymentTypes'][number])}
                  onChange={() => toggleProductType(p.type)}
                />
              ))}
            </div>
          </div>
          <Input
            label="보너스 크레딧"
            type="number"
            min={1}
            value={String(draft.bonusCredits)}
            onChange={(e) => setDraft((d) => ({ ...d, bonusCredits: parseInt(e.target.value, 10) || 0 }))}
            style={{ maxWidth: 160 }}
          />
          <DatePicker
            label="시작일"
            inline
            value={draft.startsAt ? new Date(draft.startsAt) : null}
            onChange={(date) => setDraft((d) => ({ ...d, startsAt: date ? toStartIso(date) : '' }))}
          />
          <DatePicker
            label="종료일"
            inline
            value={draft.endsAt ? new Date(draft.endsAt) : null}
            onChange={(date) => setDraft((d) => ({ ...d, endsAt: date ? toEndIso(date) : '' }))}
          />
          <Toggle
            checked={draft.enabled}
            onChange={(checked) => setDraft((d) => ({ ...d, enabled: checked }))}
            label="활성화"
          />
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>취소</Button>
            <Button loading={saving} onClick={handleSaveDraft}>저장</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (deleteTarget) await handleDelete(deleteTarget)
          setDeleteTarget(null)
        }}
        title="프로모션 삭제"
        message="삭제된 프로모션은 복구할 수 없습니다. 계속하시겠습니까?"
        confirmLabel="삭제"
        variant="danger"
        loading={saving}
      />

      {/* 소급 지급 미리보기 모달 */}
      <Modal
        open={backfillTarget !== null}
        onClose={() => setBackfillTarget(null)}
        title={backfillTarget ? `소급 지급 — ${backfillTarget.name}` : '소급 지급'}
        size="md"
      >
        <div className={styles.modalContent}>
          {preview === null ? (
            <Text variant="body-sm" style={{ color: 'var(--color-text-muted)' }}>미리보기 로딩 중…</Text>
          ) : preview.totalUsers === 0 ? (
            <Alert variant="info">소급 지급 대상이 없습니다.</Alert>
          ) : (
            <>
              <Table<BackfillPreviewUser>
                columns={[
                  { key: 'name', label: '이름', render: (row) => row.name ?? '-' },
                  { key: 'email', label: '이메일' },
                  { key: 'missedPayments', label: '미지급 건수', width: '90px' },
                  { key: 'creditsToGrant', label: '지급 크레딧', width: '90px' },
                ]}
                data={preview.users}
                rowKey={(row) => row.userId}
              />
              <Text variant="body-sm">
                합계: {preview.totalUsers}명 / {preview.totalCredits}크레딧 지급 예정
              </Text>
            </>
          )}
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setBackfillTarget(null)}>취소</Button>
            <Button
              loading={applying}
              disabled={preview === null || preview.totalUsers === 0}
              onClick={confirmBackfill}
            >
              {applying ? '지급 중…' : '지급'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ── 메인 설정 페이지 ─────────────────────────────────────────
export function Settings() {
  const { addToast } = useToast()
  const [categoryFilter, setCategoryFilter] = useState('')

  const params = new URLSearchParams()
  if (categoryFilter) params.set('category', categoryFilter)

  const { data, isLoading, error, refetch } = useApiFetch<SettingsResponse>(
    `/api/admin/settings?${params.toString()}`
  )

  const { trigger, isLoading: patchLoading } = useApiMutation('/api/admin/settings', 'PATCH')

  // 모달 상태
  const [modalOpen, setModalOpen] = useState(false)
  const [editKey, setEditKey] = useState('')
  const [editValue, setEditValue] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [editLabel, setEditLabel] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isNew, setIsNew] = useState(false)

  const categories = data ? [...new Set(data.map((s) => s.category))].sort() : []

  const openEdit = useCallback((setting: AppSetting) => {
    setEditKey(setting.key)
    setEditValue(typeof setting.value === 'string' ? setting.value : JSON.stringify(setting.value))
    setEditCategory(setting.category)
    setEditLabel(setting.label)
    setEditDescription(setting.description ?? '')
    setIsNew(false)
    setModalOpen(true)
  }, [])

  const openNew = useCallback(() => {
    setEditKey('')
    setEditValue('')
    setEditCategory('')
    setEditLabel('')
    setEditDescription('')
    setIsNew(true)
    setModalOpen(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!editKey || !editCategory || !editLabel) {
      addToast({ type: 'error', message: 'key, category, label은 필수입니다.' })
      return
    }

    let parsedValue: unknown = editValue
    try {
      parsedValue = JSON.parse(editValue)
    } catch {
      // 문자열 그대로 사용
    }

    const body: Record<string, unknown> = {
      key: editKey,
      value: parsedValue,
      category: editCategory,
      label: editLabel,
    }
    if (editDescription) body.description = editDescription

    const result = await trigger(body)
    if (result) {
      addToast({ type: 'success', message: '설정이 저장되었습니다.' })
      setModalOpen(false)
      refetch()
    } else {
      addToast({ type: 'error', message: '설정 저장에 실패했습니다.' })
    }
  }, [editKey, editValue, editCategory, editLabel, editDescription, trigger, addToast, refetch])

  const columns: TableColumn<AppSetting>[] = [
    { key: 'category', label: '카테고리', width: '100px' },
    { key: 'key', label: 'Key', render: (row) => <span className={styles.keyCell}>{row.key}</span> },
    { key: 'label', label: '라벨' },
    {
      key: 'value',
      label: '값',
      render: (row) => {
        const str = typeof row.value === 'string' ? row.value : JSON.stringify(row.value)
        return <span className={styles.valueCell}>{str.length > 50 ? str.slice(0, 50) + '...' : str}</span>
      },
    },
    {
      key: 'actions',
      label: '',
      width: '60px',
      render: (row) => (
        <button className={styles.editLink} onClick={() => openEdit(row)}>편집</button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      <MaintenanceSettings />
      <RegistrationSettings />
      <WreathSettings />
      <WatermarkSettings />
      <PromotionSettings />
      <RevenueReportRecipients />

      <Text variant="heading-sm">전체 설정</Text>
      <div className={styles.toolbar}>
        <Select
          size="sm"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          options={[
            { value: '', label: '전체 카테고리' },
            ...categories.map((c) => ({ value: c, label: c })),
          ]}
        />
        <Button size="sm" onClick={openNew}>새 설정 추가</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AppSetting>
        columns={columns}
        data={data ?? []}
        rowKey={(row) => row.key}
        isLoading={isLoading}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={isNew ? '새 설정 추가' : '설정 편집'} size="sm">
        <div className={styles.modalContent}>
          <Input
            label="Key"
            value={editKey}
            onChange={(e) => setEditKey(e.target.value)}
            disabled={!isNew}
          />
          <Input
            label="카테고리"
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value)}
          />
          <Input
            label="라벨"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
          />
          <Input
            label="값 (JSON 또는 문자열)"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
          <Input
            label="설명 (선택)"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>취소</Button>
            <Button loading={patchLoading} onClick={handleSave}>저장</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
